import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Content from '../../../layouts/adminLayouts/Content/Content';
import Actions from '../../../layouts/adminLayouts/Actions/Actions';
import type { AppDispatch, RootState } from '../../../../store/store';
import {
    adminFetchReviews,
    adminSetReviewPublished,
    adminReplyToReview,
    adminCreateFakeReview,
    adminGetGlobalReviewsEnabled,
    adminSetGlobalReviewsEnabled,
    type AdminReviewItem,
    type AdminReviewsQuery,
} from '../../../../store/ReviewsSlice';
import Popup from '../../../shared/Popup/Popup';
import './feedbackPage.scss';

type TabKey = 'messages' | 'comments';
type ModerationStatus = 'pending' | 'published' | 'rejected' | 'deleted' | 'all';

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const formatDateRu = (iso: string) => {
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return iso;
    return new Intl.DateTimeFormat('ru-RU', { year: 'numeric', month: 'short', day: '2-digit' }).format(new Date(t));
};
const normalizeSpaces = (s: string) => s.replace(/\s+/g, ' ').trim();
const cut = (s: string, max: number) => (s.length > max ? s.slice(0, max) : s);
const statusMeta = (s: AdminReviewItem['status']) => {
    if (s === 'published') return { text: 'Опубликован', cls: 'feedbackPage__status--published' as const };
    if (s === 'pending') return { text: 'На модерации', cls: 'feedbackPage__status--pending' as const };
    if (s === 'rejected') return { text: 'Отклонён', cls: 'feedbackPage__status--rejected' as const };
    return { text: 'Удалён автором', cls: 'feedbackPage__status--deleted' as const };
};

const FeedbackPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [tab, setTab] = useState<TabKey>('comments');
    const products = useSelector((s: RootState) => s.product.products);
    const adminList = useSelector((s: RootState) => s.reviews.admin?.items ?? []);
    const adminTotal = useSelector((s: RootState) => s.reviews.admin?.total ?? 0);
    const adminLoading = useSelector((s: RootState) => Boolean(s.reviews.admin?.loading));
    const globalEnabled = useSelector((s: RootState) => s.reviews.admin?.globalReviewsEnabled);
    const [status, setStatus] = useState<ModerationStatus>('pending');
    const [qText, setQText] = useState<string>('');
    const [qProduct, setQProduct] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const pageSize = 20;
    const pages = useMemo(() => {
        const p = Math.ceil(adminTotal / pageSize);
        return clamp(p || 1, 1, 10_000);
    }, [adminTotal]);
    const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
    const [busyById, setBusyById] = useState<Record<string, boolean>>({});
    const [fakeProductId, setFakeProductId] = useState<string>('');
    const [fakeAuthor, setFakeAuthor] = useState<string>('');
    const [fakeRating, setFakeRating] = useState<number>(5);
    const [fakeText, setFakeText] = useState<string>('');
    const [fakePros, setFakePros] = useState<string>('');
    const [fakeCons, setFakeCons] = useState<string>('');
    const [fakePublishNow, setFakePublishNow] = useState<boolean>(true);
    const selectedProductName = useMemo(() => {
        if (!fakeProductId) return '';
        const p = products.find((x) => String(x.id) === String(fakeProductId));
        return p?.name ? String(p.name) : '';
    }, [fakeProductId, products]);
    const query: AdminReviewsQuery = useMemo(() => {
        return {
            status: status === 'all' ? undefined : status,
            productId: qProduct ? qProduct : undefined,
            q: qText ? qText : undefined,
            page,
            limit: pageSize,
        };
    }, [status, qProduct, qText, page]);
    const [helpOpen, setHelpOpen] = useState(false);

    useEffect(() => {
        dispatch(adminGetGlobalReviewsEnabled());
    }, [dispatch]);
    useEffect(() => {
        dispatch(adminFetchReviews(query));
    }, [dispatch, query]);
    useEffect(() => {
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, qProduct, qText]);

    const refresh = async () => {
        await dispatch(adminFetchReviews(query));
    };

    const onToggleGlobal = async (next: boolean) => {
        if (busyById.__global) return;
        setBusyById((p) => ({ ...p, __global: true }));
        try {
            const a = await dispatch(adminSetGlobalReviewsEnabled({ enabled: next }));
            if ((a as unknown as { meta?: { requestStatus?: string } })?.meta?.requestStatus === 'fulfilled') {
                await dispatch(adminGetGlobalReviewsEnabled());
            }
        } finally {
            setBusyById((p) => ({ ...p, __global: false }));
        }
    };

    const publishToggle = async (r: AdminReviewItem) => {
        if (busyById[r.id]) return;
        setBusyById((p) => ({ ...p, [r.id]: true }));
        try {
            const nextPublished = r.status !== 'published';
            await dispatch(adminSetReviewPublished({ reviewId: r.id, published: nextPublished }));
            await refresh();
        } finally {
            setBusyById((p) => ({ ...p, [r.id]: false }));
        }
    };

    const saveReply = async (r: AdminReviewItem) => {
        const draft = normalizeSpaces(replyDrafts[r.id] ?? '');
        if (!draft) return;
        if (busyById[`reply:${r.id}`]) return;
        setBusyById((p) => ({ ...p, [`reply:${r.id}`]: true }));
        try {
            await dispatch(adminReplyToReview({ reviewId: r.id, text: draft }));
            setReplyDrafts((p) => ({ ...p, [r.id]: '' }));
            await refresh();
        } finally {
            setBusyById((p) => ({ ...p, [`reply:${r.id}`]: false }));
        }
    };

    const createFake = async () => {
        const pid = String(fakeProductId || '').trim();
        if (!pid) return;
        const author = cut(normalizeSpaces(fakeAuthor || ''), 40);
        const text = cut(normalizeSpaces(fakeText || ''), 1500);
        const pros = cut(normalizeSpaces(fakePros || ''), 200);
        const cons = cut(normalizeSpaces(fakeCons || ''), 200);
        if (!text && !pros && !cons) return;
        if (busyById.__fake) return;
        setBusyById((p) => ({ ...p, __fake: true }));
        try {
            await dispatch(
                adminCreateFakeReview({
                    productId: pid,
                    payload: {
                        author: author || 'Покупатель',
                        rating: clamp(fakeRating, 1, 5),
                        text,
                        pros: pros || undefined,
                        cons: cons || undefined,
                        publishNow: fakePublishNow,
                    },
                }),
            );
            setFakeAuthor('');
            setFakeRating(5);
            setFakeText('');
            setFakePros('');
            setFakeCons('');
            await refresh();
        } finally {
            setBusyById((p) => ({ ...p, __fake: false }));
        }
    };

    const productMetaById = useMemo(() => {
        const m = new Map<string, { name: string; articleNumber: string; slug: string }>();
        for (const p of products) {
            m.set(String(p.id), {
                name: String(p.name || '').trim(),
                articleNumber: String((p as any).articleNumber || '').trim(),
                slug: String((p as any).slug || '').trim(),
            });
        }
        return m;
    }, [products]);

    return (
        <div className="feedbackPage">
            <Actions title={'Обратная связь'} />
            <Content>
                <div className="feedbackPage__content">
                    <div className="feedbackPage__tabs" role="tablist" aria-label="Разделы обратной связи">
                        <button
                            type="button"
                            className={`feedbackPage__tab ${tab === 'comments' ? 'feedbackPage__tab--active' : ''}`}
                            onClick={() => setTab('comments')}
                            role="tab"
                            aria-selected={tab === 'comments'}
                        >
                            Отзывы
                        </button>
                        <button
                            type="button"
                            className={`feedbackPage__tab ${tab === 'messages' ? 'feedbackPage__tab--active' : ''}`}
                            onClick={() => setTab('messages')}
                            role="tab"
                            aria-selected={tab === 'messages'}
                        >
                            Сообщения
                        </button>
                    </div>

                    {tab === 'messages' ? (
                        <section className="feedbackPage__panel" aria-label="Сообщения">
                            <div className="feedbackPage__panelHead">
                                <div className="feedbackPage__panelTitle">Сообщения</div>
                                <div className="feedbackPage__panelHint">
                                    Заглушка. Здесь будут обращения/сообщения с формы сайта от авторизованных пользователей.
                                </div>
                            </div>

                            <div className="feedbackPage__placeholder">
                                <div className="feedbackPage__placeholderTitle">Скоро здесь появятся сообщения</div>
                                <div className="feedbackPage__placeholderText">Пока это просто блок-заготовка, чтобы занять место в админ-панели.</div>
                            </div>
                        </section>
                    ) : (
                        <section className="feedbackPage__panel" aria-label="Комментарии и отзывы">
                            <div className="feedbackPage__panelHead">
                                <div>
                                    <div className="feedbackPage__panelTitleRow">
                                        <div className="feedbackPage__panelTitle">Отзывы</div>

                                        <button
                                            type="button"
                                            className="feedbackPage__helpIcon"
                                            aria-label="Памятка администратору: работа с отзывами"
                                            onClick={() => setHelpOpen((v) => !v)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                                                <path
                                                    d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                />
                                                <path d="M12 10.5v6.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                                <path d="M12 7.6h.01" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>

                                    <Popup isOpen={helpOpen} onClose={() => setHelpOpen(false)}>
                                        <div className="feedbackPage__helpPopup">
                                            <div className="feedbackPage__helpPopupTitle">Памятка администратору: работа с отзывами</div>
                                            <div className="feedbackPage__helpPopupText">
                                                Отзывы могут быть включены или выключены глобально для всех товаров одной кнопкой.
                                            </div>
                                            <div className="feedbackPage__helpPopupText">
                                                Новый отзыв от покупателя сначала попадает на модерацию и не виден другим.
                                            </div>
                                            <div className="feedbackPage__helpPopupText">Чтобы отзыв появился на сайте - нажмите «Опубликовать».</div>
                                            <div className="feedbackPage__helpPopupText">Опубликованный отзыв всегда можно снять с публикации.</div>
                                            <div className="feedbackPage__helpPopupText">Отвечать от имени магазина можно только на опубликованные отзывы.</div>
                                            <div className="feedbackPage__helpPopupText">Ответ магазина сразу виден всем покупателям под отзывом.</div>
                                            <div className="feedbackPage__helpPopupText">Отзывы можно фильтровать по статусу, товару и тексту.</div>
                                            <div className="feedbackPage__helpPopupText">Отзывы с пометкой «Покупка подтверждена» - от реальных заказов.</div>
                                            <div className="feedbackPage__helpPopupText">
                                                Можно создать фейковый отзыв (без покупки), при необходимости опубликовать сразу.
                                            </div>
                                            <div className="feedbackPage__helpPopupText">
                                                Удалённые пользователем отзывы сохраняются в системе и видны администратору.
                                            </div>
                                        </div>
                                    </Popup>
                                    <div className="feedbackPage__panelHint">
                                        Модерация отзывов, ответы от магазина, создание фейковых отзывов и глобальное включение/выключение.
                                    </div>
                                </div>

                                <div className="feedbackPage__globalToggle">
                                    <div className="feedbackPage__globalToggleLabel">
                                        Отзывы для всех товаров:
                                        {/* <b>{globalEnabled === true ? 'включены' : globalEnabled === false ? 'отключены' : '-'}</b> */}
                                    </div>
                                    <div className="feedbackPage__switchRow">
                                        <button
                                            type="button"
                                            className={`feedbackPage__switch ${globalEnabled === true ? 'feedbackPage__switch--on' : ''}`}
                                            onClick={() => onToggleGlobal(true)}
                                            disabled={busyById.__global || globalEnabled == null || globalEnabled === true}
                                            title="Включить отзывы для всех товаров"
                                        >
                                            Вкл
                                        </button>
                                        <button
                                            type="button"
                                            className={`feedbackPage__switch ${globalEnabled === false ? 'feedbackPage__switch--off' : ''}`}
                                            onClick={() => onToggleGlobal(false)}
                                            disabled={busyById.__global || globalEnabled === false}
                                            title="Отключить отзывы для всех товаров"
                                        >
                                            Выкл
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Фильтры */}
                            <div className="feedbackPage__filters">
                                <label className="feedbackPage__field">
                                    <span className="feedbackPage__label">Статус</span>
                                    <select className="feedbackPage__select" value={status} onChange={(e) => setStatus(e.target.value as ModerationStatus)}>
                                        <option value="pending">На модерации</option>
                                        <option value="published">Опубликованные</option>
                                        {/* <option value="rejected">Отклонённые</option> */}
                                        <option value="deleted">Удалённые</option>
                                        <option value="all">Все</option>
                                    </select>
                                </label>

                                <label className="feedbackPage__field feedbackPage__field--wide">
                                    <span className="feedbackPage__label">Поиск</span>
                                    <input
                                        className="feedbackPage__input"
                                        value={qText}
                                        onChange={(e) => setQText(e.target.value)}
                                        placeholder="Текст/автор/товар…"
                                    />
                                </label>

                                <label className="feedbackPage__field">
                                    <span className="feedbackPage__label">Товар</span>
                                    <select className="feedbackPage__select" value={qProduct} onChange={(e) => setQProduct(e.target.value)}>
                                        <option value="">Все</option>
                                        {products.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <div className="feedbackPage__filtersRight">
                                    <div className="feedbackPage__meta">
                                        Найдено: <b>{adminTotal}</b>
                                    </div>
                                </div>
                            </div>

                            {/* Список */}
                            <div className="feedbackPage__list">
                                {adminLoading ? (
                                    <div className="feedbackPage__loading">Загрузка…</div>
                                ) : adminList.length ? (
                                    adminList.map((r) => {
                                        const sm = statusMeta(r.status);
                                        const replyValue = replyDrafts[r.id] ?? '';
                                        const canReply = r.status === 'published';
                                        const replyBusy = Boolean(busyById[`reply:${r.id}`]);
                                        const publishBusy = Boolean(busyById[r.id]);
                                        const pid = String(r.productId);
                                        const meta = productMetaById.get(pid);
                                        const safeName = (meta?.name || r.productName || 'Товар').trim();
                                        const safeArticle = (meta?.articleNumber || '').trim();
                                        const safeSlug = (meta?.slug || '').trim();
                                        const productHref = safeSlug ? `/${encodeURIComponent(safeSlug)}` : pid ? `/${encodeURIComponent(pid)}` : '';

                                        return (
                                            <article key={r.id} className={`feedbackPage__card feedbackPage__card--${r.status}`}>
                                                <div className="feedbackPage__cardHead">
                                                    <div className="feedbackPage__cardLeft">
                                                        <div className="feedbackPage__cardTitle">
                                                            {/* <span className="feedbackPage__productName">{safeName}</span> */}
                                                            <span className={`feedbackPage__status ${sm.cls}`}>{sm.text}</span>
                                                            {r.isVerifiedPurchase ? <span className="feedbackPage__badge">Покупка подтверждена</span> : null}
                                                        </div>
                                                        <div className="feedbackPage__productLinkRow">
                                                            {productHref ? (
                                                                <a
                                                                    className="feedbackPage__productLink"
                                                                    href={productHref}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    title="Открыть товар в новой вкладке"
                                                                >
                                                                    {safeName}
                                                                </a>
                                                            ) : (
                                                                <span className="feedbackPage__productLink feedbackPage__productLink--disabled">Открыть товар</span>
                                                            )}
                                                            <span className="feedbackPage__dot">•</span>
                                                            <span className="feedbackPage__muted">Артикул:</span>{' '}
                                                            <span className="feedbackPage__article">{safeArticle || '-'}</span>
                                                        </div>
                                                        <div className="feedbackPage__cardSub">
                                                            <span className="feedbackPage__muted">Автор:</span> {r.author}{' '}
                                                            <span className="feedbackPage__dot">•</span> <span className="feedbackPage__muted">Оценка:</span>{' '}
                                                            {r.rating}/5 <span className="feedbackPage__dot">•</span>{' '}
                                                            <span className="feedbackPage__muted">Дата:</span> {formatDateRu(r.createdAt)}
                                                        </div>
                                                    </div>

                                                    <div className="feedbackPage__cardActions">
                                                        <button
                                                            type="button"
                                                            className="feedbackPage__btn feedbackPage__btn--primary"
                                                            onClick={() => publishToggle(r)}
                                                            disabled={publishBusy}
                                                            title={r.status === 'published' ? 'Снять с публикации' : 'Опубликовать'}
                                                        >
                                                            {r.status === 'published' ? 'Снять' : 'Опубликовать'}
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="feedbackPage__cardBody">
                                                    <div className="feedbackPage__cardBody_wrapper">
                                                        {r.pros || r.cons ? (
                                                            <div className="feedbackPage__pc">
                                                                {r.pros ? (
                                                                    <div className="feedbackPage__pcRow">
                                                                        <span className="feedbackPage__pcLabel">Плюсы:</span>
                                                                        <span className="feedbackPage__pcValue">{r.pros}</span>
                                                                    </div>
                                                                ) : null}
                                                                {r.cons ? (
                                                                    <div className="feedbackPage__pcRow">
                                                                        <span className="feedbackPage__pcLabel">Минусы:</span>
                                                                        <span className="feedbackPage__pcValue">{r.cons}</span>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        ) : null}
                                                        <div className="feedbackPage__text">{r.text}</div>
                                                    </div>

                                                    <div className="feedbackPage__cardBody_wrapper">
                                                        {r.shopReply ? (
                                                            <div className="feedbackPage__replyView">
                                                                <div className="feedbackPage__replyTitle">
                                                                    Ответ магазина{' '}
                                                                    <span className="feedbackPage__muted">({formatDateRu(r.shopReply.createdAt)})</span>
                                                                </div>
                                                                <div className="feedbackPage__replyText">{r.shopReply.text}</div>
                                                            </div>
                                                        ) : null}

                                                        <div className={`feedbackPage__reply ${!canReply ? 'feedbackPage__reply--disabled' : ''}`}>
                                                            <div className="feedbackPage__replyHead">
                                                                <div className="feedbackPage__replyLabel">Ответ от магазина</div>
                                                                {!canReply ? (
                                                                    <div className="feedbackPage__replyHint">Отвечать можно только на опубликованный отзыв</div>
                                                                ) : null}
                                                            </div>

                                                            <textarea
                                                                className="feedbackPage__textarea"
                                                                value={replyValue}
                                                                onChange={(e) =>
                                                                    setReplyDrafts((p) => ({
                                                                        ...p,
                                                                        [r.id]: cut(e.target.value, 800),
                                                                    }))
                                                                }
                                                                rows={3}
                                                                placeholder="Например: Спасибо за отзыв! Мы уже передали информацию в отдел качества…"
                                                                disabled={!canReply || replyBusy}
                                                            />
                                                            <div className="feedbackPage__replyActions">
                                                                <button
                                                                    type="button"
                                                                    className="feedbackPage__btn"
                                                                    onClick={() => saveReply(r)}
                                                                    disabled={!canReply || replyBusy || !normalizeSpaces(replyValue)}
                                                                >
                                                                    Ответить
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="feedbackPage__btn"
                                                                    onClick={() => setReplyDrafts((p) => ({ ...p, [r.id]: '' }))}
                                                                    disabled={!replyValue || replyBusy}
                                                                >
                                                                    Очистить
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })
                                ) : (
                                    <div className="feedbackPage__empty">
                                        <div className="feedbackPage__emptyTitle">Нет отзывов</div>
                                        <div className="feedbackPage__emptyText">Попробуйте изменить фильтры или обновить список.</div>
                                    </div>
                                )}
                            </div>

                            {/* Пагинация */}
                            <div className="feedbackPage__pager">
                                <button type="button" className="feedbackPage__btn" onClick={() => setPage((p) => clamp(p - 1, 1, pages))} disabled={page <= 1}>
                                    Назад
                                </button>
                                <div className="feedbackPage__pagerInfo">
                                    Страница <b>{page}</b> из <b>{pages}</b>
                                </div>
                                <button type="button" className="feedbackPage__btn" onClick={() => setPage((p) => clamp(p + 1, 1, pages))} disabled={page >= pages}>
                                    Вперёд
                                </button>
                            </div>

                            {/* Фейковый отзыв */}
                            <div className="feedbackPage__fake">
                                <div className="feedbackPage__fakeHead">
                                    <div className="feedbackPage__fakeTitle">Фейковый отзыв</div>
                                    <div className="feedbackPage__fakeHint">Создаёт отзыв без покупки. Можно сразу опубликовать.</div>
                                </div>

                                <div className="feedbackPage__fakeGrid">
                                    <label className="feedbackPage__field feedbackPage__field--wide">
                                        <span className="feedbackPage__label">Товар</span>
                                        <select className="feedbackPage__select" value={fakeProductId} onChange={(e) => setFakeProductId(e.target.value)}>
                                            <option value="">Выберите товар</option>
                                            {products.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name}
                                                </option>
                                            ))}
                                        </select>
                                        {fakeProductId ? <div className="feedbackPage__hint">Выбран: {selectedProductName || fakeProductId}</div> : null}
                                    </label>

                                    <label className="feedbackPage__field">
                                        <span className="feedbackPage__label">Автор</span>
                                        <input
                                            className="feedbackPage__input"
                                            value={fakeAuthor}
                                            onChange={(e) => setFakeAuthor(cut(e.target.value, 40))}
                                            placeholder="Например: Анна П."
                                        />
                                    </label>

                                    <label className="feedbackPage__field">
                                        <span className="feedbackPage__label">Оценка</span>
                                        <select className="feedbackPage__select" value={fakeRating} onChange={(e) => setFakeRating(Number(e.target.value) || 5)}>
                                            {[5, 4, 3, 2, 1].map((v) => (
                                                <option key={v} value={v}>
                                                    {v}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="feedbackPage__field feedbackPage__field--wide">
                                        <span className="feedbackPage__label">Текст</span>
                                        <textarea
                                            className="feedbackPage__textarea"
                                            value={fakeText}
                                            onChange={(e) => setFakeText(cut(e.target.value, 1500))}
                                            rows={3}
                                            placeholder="Основной текст отзыва"
                                        />
                                    </label>

                                    <div>
                                        <label className="feedbackPage__field">
                                            <span className="feedbackPage__label">Плюсы</span>
                                            <input
                                                className="feedbackPage__input"
                                                value={fakePros}
                                                onChange={(e) => setFakePros(cut(e.target.value, 200))}
                                                placeholder="Коротко"
                                            />
                                        </label>

                                        <label className="feedbackPage__field">
                                            <span className="feedbackPage__label">Минусы</span>
                                            <input
                                                className="feedbackPage__input"
                                                value={fakeCons}
                                                onChange={(e) => setFakeCons(cut(e.target.value, 200))}
                                                placeholder="Коротко"
                                            />
                                        </label>
                                    </div>

                                    <div className="feedbackPage__fakeControls-wrapper">
                                        <label className="feedbackPage__check">
                                            <input type="checkbox" checked={fakePublishNow} onChange={(e) => setFakePublishNow(Boolean(e.target.checked))} />
                                            <span>Опубликовать сразу</span>
                                        </label>

                                        <div className="feedbackPage__fakeActions">
                                            <button
                                                type="button"
                                                className="feedbackPage__btn feedbackPage__btn--primary"
                                                onClick={createFake}
                                                disabled={busyById.__fake || !fakeProductId}
                                                title={!fakeProductId ? 'Выберите товар' : undefined}
                                            >
                                                Создать
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                </div>
            </Content>
        </div>
    );
};

export default FeedbackPage;
